/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Modal } from '../../shared/modals/Modal';
import { Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, ticketColors, ticketFonts } from '../../shared/theme/public-api';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { CatalogProductRow } from '../domain/catalogTypes';
import type { CatalogProductBranchStockView } from '../domain/catalogTypes';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal'
import type { OperationalConfirmModalProps } from '../../shared/modals/OperationalConfirmModal';
import { TicketMoreActionsSheet, ticketChrome as ticketUi, type TicketMoreAction } from '../../shared/ticket/public-api';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { resolveImageUrl } from '../infra/resolveImageUrl';
import { ZoomableImage } from '../../consultations/components/ZoomableImage';
import { CatalogProductDetailStateMascot } from './CatalogProductDetailStateMascot';
import { formatQtyCompact, UOM_OPTIONS, normalizeUnitOfMeasure } from '@shared/quantity';
import { OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import {
  useCatalogOverlayStore,
  type CatalogProductDetailTab,
} from '../store/useCatalogOverlayStore';
import { CatalogProductComboPanel } from './CatalogProductComboPanel';
import { CatalogProductDetailCompositionSummary } from './CatalogProductDetailCompositionSummary';
import { CatalogProductRecipePanel } from './CatalogProductRecipePanel';
import type { RecipeKind } from '../infra/recipesRemoteRepo';

type EntityOption = { id: string; name: string };

type CatalogProductDetailModalProps = {
  visible: boolean;
  product: CatalogProductRow | null;
  categoryLabel: string;
  currentBranchName: string;
  currentBranchId: string | null;
  stock: CatalogProductBranchStockView | null;
  stockLoading: boolean;
  /** La lectura de stock/costo falló — nunca significa "sin stock". */
  stockFailed?: boolean;
  /** Reintenta la lectura de stock/costo del detalle. */
  onRetryStock?: () => void;
  /** Capability `product_pharmacy_fields`: muestra principio activo / "para qué se usa". */
  pharmacyFieldsEnabled?: boolean;
  categoryRestricted?: boolean;
  canEdit: boolean;
  selectedProductMarginValue: number | null;
  selectedProductMarginPercent: number | null;
  currencyFormatter: { format: (value: number) => string };
  formatUpdatedAt: (value: string | null | undefined) => string;
  brandOptions?: EntityOption[];
  supplierOptions?: EntityOption[];
  onClose: () => void;
  onEdit: (product: CatalogProductRow) => void;
  onDuplicate: (product: CatalogProductRow) => void;
  onDeactivate: (product: CatalogProductRow) => void;
  onReactivate: (product: CatalogProductRow) => void;
  onViewHistory: (product: CatalogProductRow) => void;
  /** Capability recipes: producir terminado (stack). */
  onProduce?: (product: CatalogProductRow) => void;
  /** Capability recipes: fraccionar madre (stack). */
  onDisassemble?: (product: CatalogProductRow) => void;
  layoutWide?: boolean;
  /** Org de servicios: ocultar costo/stock en detalle. */
  hideStockPanels?: boolean;
  /** Confirmación anidada (ocultar / mostrar / duplicar) sin cerrar el detalle al cancelar. */
  confirmOverlay?: Omit<OperationalConfirmModalProps, 'visible' | 'presentation'> | null;
  /** Tras guardar BOM — refrescar fila del padre en lista. */
  onComboCompositionSaved?: () => void;
  /** Tras guardar receta — refrescar flags en lista. */
  onRecipeSaved?: () => void;
};

export const CatalogProductDetailModal = ({
  visible,
  product,
  categoryLabel,
  currentBranchName,
  currentBranchId,
  stock,
  stockLoading,
  stockFailed = false,
  onRetryStock,
  pharmacyFieldsEnabled = false,
  categoryRestricted = false,
  canEdit,
  selectedProductMarginValue,
  selectedProductMarginPercent,
  currencyFormatter,
  formatUpdatedAt,
  brandOptions = [],
  supplierOptions = [],
  onClose,
  onEdit,
  onDuplicate,
  onDeactivate,
  onReactivate,
  onViewHistory,
  onProduce,
  onDisassemble,
  layoutWide = false,
  hideStockPanels = false,
  confirmOverlay = null,
  onComboCompositionSaved,
  onRecipeSaved,
}: CatalogProductDetailModalProps) => {
  const selectedProduct = product;
  const detailTab = useCatalogOverlayStore((s) => s.selectedProductDetailTab);
  const setDetailTab = useCatalogOverlayStore((s) => s.setSelectedProductDetailTab);
  const recipeKind = useCatalogOverlayStore((s) => s.selectedProductRecipeKind);
  const setRecipeKind = useCatalogOverlayStore((s) => s.setSelectedProductRecipeKind);
  const canViewMenuOptions = useModulePermission('MenuOptions', 'view');
  const canEditMenuOptions = useModulePermission('MenuOptions', 'edit');
  const productCombosEnabled = useHasBusinessCapability('product_combos');
  const recipesCapabilityEnabled = useHasBusinessCapability('recipes');
  const [moreActionsVisible, setMoreActionsVisible] = React.useState(false);
  const [showMobileScrollCue, setShowMobileScrollCue] = React.useState(false);
  const scrollMetrics = React.useRef({ viewportHeight: 0, contentHeight: 0, offsetY: 0 });
  const updateScrollCue = React.useCallback((metrics = scrollMetrics.current) => {
    const shouldShow =
      visible &&
      selectedProduct != null &&
      !layoutWide &&
      metrics.viewportHeight > 0 &&
      metrics.contentHeight > metrics.viewportHeight + 24 &&
      metrics.offsetY + metrics.viewportHeight < metrics.contentHeight - 24;
    setShowMobileScrollCue((current) => current === shouldShow ? current : shouldShow);
  }, [layoutWide, visible, selectedProduct?.id]);
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const storefrontEnabled = usePublicStoreFeaturesVisible();
  const purchasesEnabled = useHasBusinessCapability('purchases');
  const tablesEnabled = useHasBusinessCapability('tables');
  const posEnabled = useHasBusinessCapability('pos');
  const appointmentsEnabled = useHasBusinessCapability('appointments');
  const serviceCatalogEnabled = useHasBusinessCapability('service_catalog');
  const serviceQuotesEnabled = useHasBusinessCapability('service_quotes');
  const mercadoPagoEnabled = useHasBusinessCapability('mp_oauth');
  const modalCardMaxWidth = layoutWide ? Math.min(960, winW - 48) : Math.min(520, winW - 32);
  const modalCardMaxHeight = winH * 0.92;
  const isService = selectedProduct?.itemType === 'service';
  const isMisc = selectedProduct?.isMisc === true;
  const showComposicionTab =
    productCombosEnabled &&
    canViewMenuOptions &&
    !!selectedProduct &&
    !isService &&
    !isMisc;
  const showStockPanels = !hideStockPanels && !isService;
  const showRecetaTab =
    recipesCapabilityEnabled &&
    !!selectedProduct &&
    !isService &&
    !isMisc &&
    showStockPanels;
  React.useEffect(() => {
    if (!showComposicionTab && detailTab === 'composicion') {
      setDetailTab('resumen');
    }
  }, [showComposicionTab, detailTab, setDetailTab]);
  React.useEffect(() => {
    if (!showRecetaTab && detailTab === 'receta') {
      setDetailTab('resumen');
    }
  }, [showRecetaTab, detailTab, setDetailTab]);
  const showServicePanel = isService && serviceCatalogEnabled;
  const showBookingDetails = showServicePanel && appointmentsEnabled;
  const quoteSettingsEnabled = serviceQuotesEnabled && storefrontEnabled;
  const paymentSettingsEnabled = storefrontEnabled && mercadoPagoEnabled;
  const itemNoun = isService ? 'servicio' : isMisc ? 'ítem' : 'producto';
  const itemTypeLabel = isService ? 'Servicio' : isMisc ? 'Varios' : 'Producto';
  const canManageSelectedProduct = canEdit && !isMisc;
  const barcodeDisplay =
    selectedProduct?.barcode != null && String(selectedProduct.barcode).trim() !== ''
      ? String(selectedProduct.barcode).trim()
      : null;
  const skuDisplay =
    selectedProduct?.sku != null && String(selectedProduct.sku).trim() !== ''
      ? String(selectedProduct.sku).trim()
      : null;

  React.useEffect(() => {
    setMoreActionsVisible(false);
    scrollMetrics.current.offsetY = 0;
    updateScrollCue();
  }, [visible, selectedProduct?.id, layoutWide, updateScrollCue]);

  const brandName = selectedProduct?.brandId
    ? brandOptions.find((b) => b.id === selectedProduct.brandId)?.name ?? null
    : null;
  const supplierName = selectedProduct?.preferredSupplierId
    ? supplierOptions.find((s) => s.id === selectedProduct.preferredSupplierId)?.name ?? null
    : null;
  const supplierItemCode = selectedProduct?.supplierItemCode ?? null;
  const internalNotes = selectedProduct?.internalNotes ?? null;
  const uomLabel =
    UOM_OPTIONS.find((opt) => opt.id === normalizeUnitOfMeasure(selectedProduct?.unitOfMeasure))
      ?.label ?? 'Unidad';
  const activeIngredient = pharmacyFieldsEnabled
    ? (selectedProduct?.activeIngredient?.trim() || null)
    : null;
  const usageTagsLabel =
    pharmacyFieldsEnabled && Array.isArray(selectedProduct?.usageTags)
      ? selectedProduct!.usageTags!.filter((t) => String(t).trim().length > 0).join(', ') || null
      : null;

  const dismissDetail = () => {
    setMoreActionsVisible(false);
    if (confirmOverlay) {
      confirmOverlay.onClose();
      return;
    }
    onClose();
  };

  const kvCell = (label: string, value: string, opts?: { mono?: boolean; muted?: boolean; selectable?: boolean }) => (
    <View style={styles.kvCell}>
      <Text style={styles.kvCellLabel}>{label}</Text>
      <Text
        selectable={opts?.selectable}
        style={[
          styles.kvCellValue,
          opts?.mono && styles.kvCellValueMono,
          opts?.muted && styles.kvCellValueMuted,
        ]}
      >
        {value}
      </Text>
    </View>
  );

  const heroBlock = selectedProduct ? (
    <View style={[styles.overview, !layoutWide && styles.overviewCompact]}>
      <View
        style={[
          styles.productMedia,
          layoutWide ? styles.productMediaWide : styles.productMediaCompact,
          selectedProduct.imageUrl ? styles.productMediaFilled : styles.productMediaEmpty,
        ]}
      >
        {selectedProduct.imageUrl ? (
          <ZoomableImage
            uri={resolveImageUrl(selectedProduct.imageUrl, 'full') ?? selectedProduct.imageUrl}
            style={styles.productMediaImage}
            title={selectedProduct.name}
            fill
            thumbResizeMode="cover"
          />
        ) : (
          <>
            <Ionicons name="image-outline" size={30} color={ticketColors.ink3} />
            <Text style={styles.productMediaCaption}>Sin foto cargada</Text>
          </>
        )}
      </View>
      <View style={styles.overviewMain}>
        <Text style={styles.priceLabel}>Precio de venta</Text>
        <Text style={styles.productPrice}>{currencyFormatter.format(selectedProduct.price)}</Text>
      </View>
    </View>
  ) : null;

  const classificationBlock = selectedProduct ? (
    <View style={[styles.detailSection, layoutWide ? styles.detailGridCellWide : styles.detailGridCellCompact]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>{isService ? 'Datos del servicio' : 'Datos del producto'}</Text>
      </View>
      <View style={styles.kvGrid}>
        {kvCell('Categoría', categoryLabel || 'Sin categoría')}
        {kvCell('Marca', brandName ?? 'Sin cargar', { muted: !brandName })}
        {kvCell('Unidad de medida', uomLabel)}
      </View>
    </View>
  ) : null;

  const notesBlock = internalNotes ? (
    <View style={[styles.detailSection, layoutWide ? styles.detailGridCellWideFull : styles.detailGridCellCompact]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>Notas internas</Text>
      </View>
      <Text style={styles.infoLine}>{internalNotes}</Text>
    </View>
  ) : null;

  // Farmacia: campos del capability `product_pharmacy_fields` — sin fork por rubro.
  const pharmacyBlock =
    selectedProduct && (activeIngredient || usageTagsLabel) ? (
      <View style={[styles.detailSection, layoutWide ? styles.detailGridCellWide : styles.detailGridCellCompact]}>
        <View style={styles.sectionHeadingRow}>
          <Text style={styles.sectionTitle}>Farmacia</Text>
        </View>
        <View style={styles.kvGrid}>
          {activeIngredient
            ? kvCell('Principio activo', activeIngredient, { selectable: true })
            : null}
          {usageTagsLabel ? kvCell('Para qué se usa', usageTagsLabel) : null}
        </View>
      </View>
    ) : null;

  const identificationBlock = selectedProduct ? (
    <View style={[styles.detailSection, layoutWide ? styles.detailGridCellWide : styles.detailGridCellCompact]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>Identificación</Text>
      </View>
      <View style={styles.kvGrid}>
        {kvCell('Código de barras', barcodeDisplay ?? 'No registrado', {
          mono: !!barcodeDisplay,
          muted: !barcodeDisplay,
          selectable: true,
        })}
        {kvCell('SKU', skuDisplay ?? 'No registrado', {
          mono: !!skuDisplay,
          muted: !skuDisplay,
          selectable: true,
        })}
        {kvCell('Creado', formatUpdatedAt(selectedProduct.createdAt))}
        {kvCell('Actualizado', formatUpdatedAt(selectedProduct.updatedAt))}
      </View>
    </View>
  ) : null;

  const purchaseBlock = selectedProduct && purchasesEnabled ? (
    <View style={[styles.detailSection, layoutWide ? styles.detailGridCellWide : styles.detailGridCellCompact]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>Compras</Text>
      </View>
      <View style={styles.kvGrid}>
        {kvCell('Proveedor', supplierName ?? 'Sin cargar', { muted: !supplierName })}
        {kvCell('Código del proveedor', supplierItemCode || '—', {
          mono: Boolean(supplierItemCode),
          muted: !supplierItemCode,
          selectable: true,
        })}
      </View>
    </View>
  ) : null;

  const channelRows: Array<{
    key: string;
    label: string;
    value: string;
    tone: 'ok' | 'warn' | 'off';
  }> = [];
  if (selectedProduct) {
    const active = selectedProduct.isActive;
    if (storefrontEnabled) {
      const published = active && selectedProduct.isPublic !== false;
      channelRows.push({
        key: 'online',
        label: 'Tienda online',
        value: published ? 'Publicada' : 'No publicada',
        tone: published ? 'ok' : 'off',
      });
    }
    if (tablesEnabled && !isService) {
      const visible = active && !categoryRestricted && selectedProduct.visibleOnMenuCarta !== false;
      channelRows.push({
        key: 'qr',
        label: 'Carta QR',
        value: !active ? 'No visible' : categoryRestricted ? 'No se muestra · +18' : visible ? 'Visible' : 'No visible',
        tone: active && categoryRestricted ? 'warn' : visible ? 'ok' : 'off',
      });
    }
    if (posEnabled || storefrontEnabled || (tablesEnabled && !isService)) {
      const available = active && selectedProduct.menuAvailable !== false;
      channelRows.push({
        key: 'today',
        label: 'Disponible hoy',
        value: available ? 'Sí' : 'No',
        tone: available ? 'ok' : 'off',
      });
    }
  }
  const showAgeGateNote = Boolean(
    selectedProduct &&
      storefrontEnabled &&
      categoryRestricted &&
      selectedProduct.isActive &&
      selectedProduct.isPublic !== false,
  );
  const channelsBlock = selectedProduct && channelRows.length > 0 ? (
    <View style={[styles.detailSection, styles.channelSection]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>Publicación y disponibilidad</Text>
      </View>
      <View style={layoutWide ? styles.channelListWide : styles.channelListCompact}>
        {channelRows.map((row, index) => (
          <View
            key={row.key}
            style={[
              styles.channelRow,
              layoutWide ? styles.channelRowWide : styles.channelRowCompact,
              layoutWide && index < channelRows.length - 1 && styles.channelRowDivider,
            ]}
          >
            <Text style={styles.channelName}>{row.label}</Text>
            <View style={styles.channelStatus}>
              <View
                style={[
                  styles.channelDot,
                  row.tone === 'ok' && styles.channelDotOk,
                  row.tone === 'warn' && styles.channelDotWarn,
                  row.tone === 'off' && styles.channelDotOff,
                ]}
              />
              <Text
                style={[
                  styles.channelValue,
                  row.tone === 'warn' && styles.channelValueWarn,
                  row.tone === 'off' && styles.channelValueOff,
                ]}
              >
                {row.value}
              </Text>
            </View>
          </View>
        ))}
      </View>
      {showAgeGateNote ? (
        <View style={styles.ageNote}>
          <Text style={styles.ageNoteText}>
            La tienda online pide confirmar que el cliente sea mayor de 18 al comprar. No se muestra en la carta QR.
          </Text>
        </View>
      ) : null}
    </View>
  ) : null;

  const durationLabel =
    selectedProduct?.durationMinutes != null && selectedProduct.durationMinutes > 0
      ? `${selectedProduct.durationMinutes} min`
      : 'Sin cargar';
  const bookingModeLabel =
    selectedProduct?.bookingMode === 'quote_required'
      ? quoteSettingsEnabled
        ? 'Cotización'
        : null
      : 'Reserva directa';
  const bookingPaymentLabel = (() => {
    if (selectedProduct?.bookingPaymentMode === 'none') return 'Sin cobro online';
    if (selectedProduct?.bookingPaymentMode === 'full') return 'Pago total al reservar';
    if (selectedProduct?.bookingPaymentMode === 'deposit') {
      const depositParts = [
        selectedProduct.bookingDepositAmount != null && selectedProduct.bookingDepositAmount > 0
          ? currencyFormatter.format(selectedProduct.bookingDepositAmount)
          : null,
        selectedProduct.bookingDepositPercent != null && selectedProduct.bookingDepositPercent > 0
          ? `${selectedProduct.bookingDepositPercent}%`
          : null,
      ].filter(Boolean);
      return depositParts.length > 0 ? `Seña ${depositParts.join(' · ')}` : 'Seña según la tienda';
    }
    return 'Como la tienda';
  })();
  const serviceBlock = selectedProduct && showServicePanel ? (
    <View style={[styles.detailSection, styles.operationsPanel]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>{appointmentsEnabled ? 'Reserva y cobro' : 'Detalles del servicio'}</Text>
      </View>
      <View style={styles.kvGrid}>
        {kvCell('Duración', durationLabel, { muted: durationLabel === 'Sin cargar' })}
        {showBookingDetails && bookingModeLabel
          ? kvCell('Reserva', bookingModeLabel)
          : null}
        {showBookingDetails && paymentSettingsEnabled
          ? kvCell('Cobro online', bookingPaymentLabel)
          : null}
      </View>
    </View>
  ) : null;

  const metricTile = (label: string, value: string, tone?: 'ok' | 'bad', last = false) => (
    <View style={[styles.metric, last && styles.metricLast]}>
      <Text style={styles.metricLabel}>{label}</Text>
      <Text
        style={[
          styles.metricValue,
          !layoutWide && styles.metricValueCompact,
          tone === 'ok' && styles.metricValueOk,
          tone === 'bad' && styles.metricValueBad,
        ]}
      >
        {value}
      </Text>
    </View>
  );

  const stockFeedback = (
    mood: 'thinking' | 'confused' | 'sleeping',
    message: string,
    retry = false,
  ) => (
    <View style={[styles.stockFeedback, mood === 'confused' && styles.stockFeedbackError]}>
      <CatalogProductDetailStateMascot
        mood={mood}
        testID={`catalog.detail.stock-mascot.${mood}`}
      />
      <View style={styles.stockFeedbackContent}>
        <Text
          style={[styles.stockFeedbackText, mood === 'confused' && styles.stockFeedbackErrorText]}
          accessibilityLiveRegion={mood === 'confused' ? 'assertive' : 'polite'}
        >
          {message}
        </Text>
        {retry && onRetryStock ? (
          <TouchableOpacity
            style={ticketUi.btnGhost}
            onPress={onRetryStock}
            accessibilityRole="button"
            accessibilityLabel="Reintentar lectura de stock"
            testID="catalog.detail.stock-retry"
          >
            <Text style={ticketUi.btnGhostText}>Reintentar</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );

  const costMarginBlock = selectedProduct && showStockPanels ? (
    <View style={[styles.detailSection, styles.operationsPanel]}>
      <View style={styles.sectionHeadingRow}>
        <Text style={styles.sectionTitle}>Stock y rentabilidad</Text>
        <Text style={styles.sectionMeta}>Por sucursal</Text>
      </View>
      {!currentBranchId ? (
        <Text style={styles.infoLine}>Seleccioná una sucursal para ver el costo y el margen.</Text>
      ) : (() => {
        const isComboKit = selectedProduct.isCombo === true;
        const stockReady = !stockLoading;
        const showComboProfitability = isComboKit && stockReady && !stockFailed;
        const showStockProfitability = !isComboKit && stockReady && !stockFailed && stock != null;
        if (stockLoading && !isComboKit) {
          return stockFeedback('thinking', 'Calculando datos de stock…');
        }
        if (stockFailed && !isComboKit) {
          return stockFeedback('confused', 'No pudimos verificar el stock en esta sucursal.', true);
        }
        if (!showComboProfitability && !showStockProfitability) {
          if (isComboKit && stockLoading) {
            return stockFeedback('thinking', 'Calculando costo de composición…');
          }
          return stockFeedback(
            'sleeping',
            isComboKit
              ? 'No pudimos calcular el costo de composición en esta sucursal.'
              : 'No hay stock cargado en esta sucursal para calcular costo y margen.',
          );
        }
        return (
        <>
          {(() => {
            const unitCost = isComboKit
              ? selectedProduct.comboReferenceUnitCost
              : stock!.avgCost > 0
                ? stock!.avgCost
                : null;
            return (
              <>
          <View style={styles.metricRow}>
            {metricTile(
              isComboKit ? 'Costo de referencia' : 'Costo promedio',
              unitCost != null && unitCost > 0
                ? currencyFormatter.format(unitCost)
                : isComboKit && selectedProduct.comboReferenceComplete === false
                  ? 'Faltan costos'
                  : 'Sin cargar',
            )}
            {unitCost != null && unitCost > 0
              ? metricTile(
                  'Margen bruto',
                  selectedProductMarginPercent != null
                    ? `${selectedProductMarginPercent.toFixed(1)}%`
                    : selectedProductMarginValue != null
                      ? currencyFormatter.format(selectedProductMarginValue)
                      : '—',
                  selectedProductMarginValue != null && selectedProductMarginValue < 0
                    ? 'bad'
                    : selectedProductMarginPercent != null && selectedProductMarginPercent > 0
                      ? 'ok'
                      : undefined,
                )
              : null}
            {isComboKit
              ? metricTile(
                  'Podés vender',
                  selectedProduct.comboSellableQty != null
                    ? `~${formatQtyCompact(selectedProduct.comboSellableQty, selectedProduct.unitOfMeasure)}`
                    : '—',
                  undefined,
                  true,
                )
              : metricTile(
                  'Stock',
                  formatQtyCompact(stock!.quantity, selectedProduct.unitOfMeasure),
                  undefined,
                  true,
                )}
          </View>
          {isComboKit && showComposicionTab ? (
            <CatalogProductDetailCompositionSummary
              productId={selectedProduct.id}
              onViewComposition={() => setDetailTab('composicion')}
            />
          ) : null}
          {isComboKit ? (
            unitCost != null && unitCost > 0 ? (
              <Text style={styles.infoLine}>
                Costo de referencia = suma de partes × costo promedio en esta sucursal.
              </Text>
            ) : (
              <Text style={styles.infoLine}>
                Cargá costo promedio en cada parte para ver referencia y margen.
              </Text>
            )
          ) : unitCost != null && unitCost > 0 ? null : (
            <Text style={styles.infoLine}>
              Sin costo cargado: el margen se calcula cuando haya costo promedio.
            </Text>
          )}
              </>
            );
          })()}
        </>
        );
      })()}
    </View>
  ) : null;

  const moreActions: TicketMoreAction[] = [];
  if (selectedProduct) {
    if (!layoutWide) {
      moreActions.push({
        key: 'history',
        label: 'Historial',
        icon: 'time-outline',
        onPress: () => {
          onClose();
          onViewHistory(selectedProduct);
        },
        testID: 'catalog.detail.history',
      });
    }
    if (canManageSelectedProduct) {
      moreActions.push({
        key: 'duplicate',
        label: 'Duplicar',
        icon: 'copy-outline',
        onPress: () => onDuplicate(selectedProduct),
        testID: 'catalog.detail.duplicate',
      });
    }
    if (showRecetaTab) {
      moreActions.push({
        key: 'recipe-tab',
        label: 'Ver receta',
        icon: 'book-outline',
        onPress: () => setDetailTab('receta'),
        testID: 'catalog.detail.recipeTab',
      });
    }
    if (showStockPanels && onProduce) {
      moreActions.push({
        key: 'produce',
        label: 'Producir lote',
        icon: 'flask-outline',
        onPress: () => {
          onClose();
          onProduce(selectedProduct);
        },
        testID: 'catalog.detail.produce',
      });
    }
    if (showStockPanels && onDisassemble) {
      moreActions.push({
        key: 'disassemble',
        label: 'Fraccionar stock',
        icon: 'cut-outline',
        onPress: () => {
          onClose();
          onDisassemble(selectedProduct);
        },
        testID: 'catalog.detail.disassemble',
      });
    }
    if (canManageSelectedProduct && selectedProduct.isActive) {
      moreActions.push({
        key: 'deactivate',
        label: 'Desactivar',
        icon: 'eye-off-outline',
        onPress: () => onDeactivate(selectedProduct),
        testID: 'catalog.detail.deactivate',
      });
    }
    if (canManageSelectedProduct && !selectedProduct.isActive) {
      moreActions.push({
        key: 'activate',
        label: 'Activar',
        icon: 'eye-outline',
        onPress: () => onReactivate(selectedProduct),
        testID: 'catalog.detail.activate',
      });
    }
  }

  const actionsBlock = () => {
    if (!selectedProduct) return null;

    return (
      <View style={[ticketUi.foot, styles.detailFooter]}>
        <View style={styles.footerLeft}>
          {canManageSelectedProduct ? (
            <TouchableOpacity
              style={[ticketUi.btnGold, styles.footerButton, !layoutWide && styles.footerEditCompact]}
              onPress={() => {
                onClose();
                onEdit(selectedProduct);
              }}
              accessibilityRole="button"
              accessibilityLabel={`Editar ${itemNoun}`}
              testID="catalog.detail.edit"
            >
              <Text style={ticketUi.btnGoldText}>Editar</Text>
            </TouchableOpacity>
          ) : null}
          {layoutWide ? (
            <TouchableOpacity
              style={[ticketUi.btnGhost, styles.footerButton]}
              onPress={() => {
                onClose();
                onViewHistory(selectedProduct);
              }}
              accessibilityRole="button"
              accessibilityLabel={`Historial de ${itemNoun}`}
              testID="catalog.detail.history"
            >
              <Text style={ticketUi.btnGhostText}>Historial</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {moreActions.length > 0 ? (
          <TouchableOpacity
            style={[ticketUi.btnGhost, styles.moreActionsButton]}
            onPress={() => setMoreActionsVisible(true)}
            accessibilityRole="button"
            accessibilityLabel="Más acciones"
            accessibilityHint={`Otras acciones para ${itemNoun}`}
            accessibilityState={{ expanded: moreActionsVisible }}
            testID="catalog.detail.moreActions"
          >
            <Text style={ticketUi.btnGhostText}>Más acciones</Text>
            <Ionicons name="chevron-down" size={16} color={ticketColors.ink2} />
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const moreActionsSheet = (
    <TicketMoreActionsSheet
      visible={visible && moreActionsVisible && moreActions.length > 0}
      title="Más acciones"
      actions={moreActions}
      onClose={() => setMoreActionsVisible(false)}
    />
  );

  // El encabezado concentra el nombre y el contexto de la ficha.
  const headerTitle = selectedProduct?.name ?? (isService ? 'Detalle de servicio' : 'Detalle de producto');
  const headerContext = [itemTypeLabel, currentBranchName].filter(Boolean).join(' · ');
  const statusBadge = selectedProduct ? (
    <View style={[styles.statusBadge, selectedProduct.isActive ? styles.statusBadgeActive : styles.statusBadgeInactive]}>
      <View style={[styles.statusDot, selectedProduct.isActive ? styles.statusDotActive : styles.statusDotInactive]} />
      <Text style={[styles.statusBadgeText, selectedProduct.isActive ? styles.statusBadgeTextActive : styles.statusBadgeTextInactive]}>
        {selectedProduct.isActive ? 'Activo' : 'Inactivo'}
      </Text>
    </View>
  ) : null;

  const closeGlyph = (
    <TouchableOpacity
      onPress={dismissDetail}
      style={[ticketUi.sheetCloseBtn, styles.detailCloseButton]}
      activeOpacity={0.6}
      accessibilityRole="button"
      accessibilityLabel="Cerrar el detalle"
    >
      <Text style={ticketUi.sheetCloseGlyph}>×</Text>
    </TouchableOpacity>
  );

  // Overlay al root del Modal (no adentro del card): overflow/clip del sheet
  // hacía que el confirm no se viera o no recibiera taps — mismo patrón que ModalShell.
  const confirmLayer = confirmOverlay ? (
    <View
      pointerEvents="box-none"
      testID="catalog.detail.confirm-overlay"
      style={[StyleSheet.absoluteFill, styles.confirmLayer]}
    >
      <OperationalConfirmModal visible presentation="overlay" {...confirmOverlay} />
    </View>
  ) : null;

  const detailTabOptions = React.useMemo(() => {
    const opts: Array<{ value: CatalogProductDetailTab; label: string }> = [
      { value: 'resumen', label: 'Resumen' },
    ];
    if (showComposicionTab) opts.push({ value: 'composicion', label: 'Composición (venta)' });
    if (showRecetaTab) opts.push({ value: 'receta', label: 'Producción (receta)' });
    return opts;
  }, [showComposicionTab, showRecetaTab]);

  const detailTabSwitcher =
    detailTabOptions.length > 1 ? (
      <View style={styles.detailTabRow}>
        <OperationalFilterSegmentedRow<CatalogProductDetailTab>
          ticketTone
          options={detailTabOptions}
          value={detailTab}
          onChange={setDetailTab}
          testIDPrefix="catalog.detail.tab"
        />
      </View>
    ) : null;

  const compositionPanel =
    selectedProduct && detailTab === 'composicion' ? (
      <CatalogProductComboPanel
        productId={selectedProduct.id}
        productName={selectedProduct.name}
        presentation="detail"
        canEdit={canEdit && canEditMenuOptions}
        salePrice={selectedProduct.price}
        formatMoney={currencyFormatter.format}
        onSaved={onComboCompositionSaved}
      />
    ) : null;

  const recipePanel =
    selectedProduct && detailTab === 'receta' ? (
      <CatalogProductRecipePanel
        productId={selectedProduct.id}
        productName={selectedProduct.name}
        kind={recipeKind as RecipeKind}
        onKindChange={(k) => setRecipeKind(k)}
        showKindSwitch
        canEdit={canEdit}
        onProduce={
          onProduce
            ? () => {
                onClose();
                onProduce(selectedProduct);
              }
            : undefined
        }
        onDisassembleRun={
          onDisassemble
            ? () => {
                onClose();
                onDisassemble(selectedProduct);
              }
            : undefined
        }
        onSaved={onRecipeSaved}
      />
    ) : null;

  const bodyScroll = selectedProduct ? (
    <ScrollView
      key={`${selectedProduct.id}-${detailTab}`}
      style={styles.detailScroll}
      contentContainerStyle={layoutWide ? styles.detailScrollContentWide : styles.sheetScrollContent}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={!layoutWide}
      nestedScrollEnabled
      onLayout={(event) => {
        scrollMetrics.current.viewportHeight = event.nativeEvent.layout.height;
        updateScrollCue();
      }}
      onContentSizeChange={(_, contentHeight) => {
        scrollMetrics.current.contentHeight = contentHeight;
        updateScrollCue();
      }}
      onScroll={(event) => {
        scrollMetrics.current.offsetY = event.nativeEvent.contentOffset.y;
        updateScrollCue();
      }}
      scrollEventThrottle={16}
    >
      {detailTabSwitcher}
      {detailTab === 'composicion' ? (
        <View style={styles.compositionTabWrap}>{compositionPanel}</View>
      ) : detailTab === 'receta' ? (
        recipePanel
      ) : (
        <>
          <View style={layoutWide ? styles.topGridWide : styles.topGridCompact}>
            <View style={styles.summaryColumn}>{heroBlock}</View>
            {costMarginBlock || serviceBlock ? (
              <View style={layoutWide ? styles.operationsColumnWide : styles.operationsColumnCompact}>
                {costMarginBlock || serviceBlock}
              </View>
            ) : null}
          </View>
          {channelsBlock}
          <View style={layoutWide ? styles.detailsGridWide : styles.detailsGridCompact}>
            {classificationBlock}
            {identificationBlock}
            {purchaseBlock}
            {pharmacyBlock}
            {notesBlock}
          </View>
        </>
      )}
    </ScrollView>
  ) : null;

  const scrollCue = !layoutWide && showMobileScrollCue ? (
    <View pointerEvents="none" style={styles.scrollCue} testID="catalog.detail.scrollCue">
      <Text style={styles.scrollCueText}>Deslizá para ver más</Text>
      <Ionicons name="chevron-down" size={16} color={ticketColors.brandPrimary} />
    </View>
  ) : null;
  const scrollArea = bodyScroll ? (
    <View style={styles.scrollArea}>
      {bodyScroll}
      {scrollCue}
    </View>
  ) : null;

  if (layoutWide) {
    return (
      <>
        <Modal
          visible={visible}
          transparent
          animationType="fade"
          onRequestClose={dismissDetail}
          accessibilityViewIsModal
        >
          <View style={[posUi.modalBackdrop, modalExperience.modalRoot, modalBackdropPadding]}>
            <View
              style={[
                ticketUi.dialogCard,
                styles.modalCard,
                { maxWidth: modalCardMaxWidth, maxHeight: modalCardMaxHeight, width: '100%' },
              ]}
              testID="catalog.detail.modal"
            >
              <View style={[styles.detailHeader, styles.detailHeaderWide]}>
                <View style={styles.detailHeaderCopy}>
                  <Text style={styles.headerKicker}>Catálogo</Text>
                  <Text style={styles.productTitle}>
                    {headerTitle}
                  </Text>
                  {headerContext ? (
                    <Text style={styles.productContext}>
                      {headerContext}
                    </Text>
                  ) : null}
                </View>
                <View style={styles.headerActions}>
                  {statusBadge}
                  {closeGlyph}
                </View>
              </View>
              {scrollArea}
              {selectedProduct ? actionsBlock() : null}
            </View>
            {confirmLayer}
          </View>
        </Modal>
        {moreActionsSheet}
      </>
    );
  }

  return (
    <>
      <Modal
        visible={visible}
        transparent
        animationType="slide"
        onRequestClose={dismissDetail}
        accessibilityViewIsModal
      >
        <View style={ticketUi.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={dismissDetail} />
          <View
            style={[ticketUi.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), flex: 1 }]}
            testID="catalog.detail.modal"
          >
            <View style={ticketUi.grabber} />
            <View style={[styles.detailHeader, styles.detailHeaderCompact]}>
              <View style={styles.detailHeaderCopy}>
                <Text style={styles.headerKicker}>Catálogo</Text>
                <Text style={[styles.productTitle, styles.productTitleCompact]}>
                  {headerTitle}
                </Text>
                {headerContext ? (
                  <Text style={styles.productContext}>
                    {headerContext}
                  </Text>
                ) : null}
              </View>
              <View style={styles.headerActions}>
                {statusBadge}
                {closeGlyph}
              </View>
            </View>
            {scrollArea}
            {selectedProduct ? actionsBlock() : null}
          </View>
          {confirmLayer}
        </View>
      </Modal>
      {moreActionsSheet}
    </>
  );
};

const styles = StyleSheet.create({
  confirmLayer: {
    zIndex: 9999,
    elevation: 9999,
    width: '100%',
    height: '100%',
    alignSelf: 'stretch',
  },
  modalCard: {
    alignSelf: 'center',
    overflow: 'hidden',
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  detailHeaderWide: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 12,
  },
  detailHeaderCompact: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 12,
  },
  detailHeaderCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  headerKicker: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: ticketColors.brandPrimary,
    fontFamily: ticketFonts.sans,
  },
  productTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: ticketColors.ink,
    lineHeight: 25,
    letterSpacing: -0.3,
    fontFamily: ticketFonts.sans,
  },
  productTitleCompact: {
    fontSize: 17,
    lineHeight: 22,
  },
  productContext: {
    marginTop: 4,
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 16,
    fontFamily: ticketFonts.sans,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  statusBadge: {
    minHeight: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderRadius: 16,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  statusBadgeActive: {
    borderColor: ticketColors.brandBorderSoft,
  },
  statusBadgeInactive: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  statusDotActive: {
    backgroundColor: ticketColors.ok,
  },
  statusDotInactive: {
    backgroundColor: ticketColors.warn,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  statusBadgeTextActive: {
    color: ticketColors.ok,
  },
  statusBadgeTextInactive: {
    color: ticketColors.warn,
  },
  detailCloseButton: {
    width: 44,
    height: 44,
  },
  detailTabRow: {
    marginBottom: spacing.sm,
  },
  compositionTabWrap: {
    width: '100%',
    alignSelf: 'stretch',
    minWidth: 0,
  },
  detailScroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  detailScrollContentWide: {
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  sheetScrollContent: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  scrollCue: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  scrollCueText: {
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  topGridWide: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 22,
  },
  topGridCompact: {
    flexDirection: 'column',
    gap: spacing.md,
  },
  summaryColumn: {
    flex: 1,
    minWidth: 0,
  },
  operationsColumnWide: {
    width: '38%',
    minWidth: 260,
  },
  operationsColumnCompact: {
    width: '100%',
  },
  overview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  overviewCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  productMedia: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
    flexShrink: 0,
  },
  // La foto del producto es protagonista: ~4:3 en desktop, full-width en mobile.
  productMediaWide: {
    width: 184,
    height: 138,
  },
  productMediaCompact: {
    width: '100%',
    height: 200,
  },
  productMediaFilled: {
    padding: 0,
  },
  productMediaEmpty: {
    backgroundColor: ticketColors.tile,
  },
  // ponytail: white frame + contain so pack shots (any aspect) stay fully visible
  productMediaImage: {
    width: '100%',
    height: '100%',
  },
  productMediaCaption: {
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  overviewMain: {
    flex: 1,
    minWidth: 0,
  },
  priceLabel: {
    color: ticketColors.ink2,
    fontSize: 12,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  productPrice: {
    marginTop: 4,
    color: ticketColors.ink,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.mono,
  },
  detailSection: {
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
    gap: spacing.sm,
    minWidth: 0,
  },
  detailGridCellWide: {
    flexBasis: '30%',
    flexGrow: 1,
    minWidth: 0,
  },
  detailGridCellWideFull: {
    flexBasis: '100%',
    minWidth: 0,
  },
  detailGridCellCompact: {
    width: '100%',
    minWidth: 0,
  },
  sectionHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: 2,
  },
  sectionTitle: {
    color: ticketColors.ink,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  sectionMeta: {
    color: ticketColors.ink3,
    fontSize: 12,
    fontFamily: ticketFonts.sans,
    textAlign: 'right',
  },
  channelSection: {
    width: '100%',
  },
  channelListWide: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  channelListCompact: {
    flexDirection: 'column',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  channelRow: {
    minWidth: 0,
  },
  channelRowWide: {
    flex: 1,
    minHeight: 56,
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  channelRowCompact: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  channelRowDivider: {
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
  },
  channelName: {
    color: ticketColors.ink2,
    fontSize: 12,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  channelStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  channelDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  channelDotOk: {
    backgroundColor: ticketColors.ok,
  },
  channelDotWarn: {
    backgroundColor: ticketColors.warn,
  },
  channelDotOff: {
    backgroundColor: ticketColors.ink3,
  },
  channelValue: {
    color: ticketColors.ink,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
  },
  channelValueWarn: {
    color: ticketColors.warn,
  },
  channelValueOff: {
    color: ticketColors.ink3,
  },
  ageNote: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    borderRadius: 8,
    backgroundColor: ticketColors.warnBg,
  },
  ageNoteText: {
    color: ticketColors.warn,
    fontSize: 12,
    lineHeight: 17,
    fontFamily: ticketFonts.sans,
  },
  detailsGridWide: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  detailsGridCompact: {
    flexDirection: 'column',
    gap: spacing.md,
  },
  operationsPanel: {
    width: '100%',
  },
  kvGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: spacing.md,
    rowGap: spacing.sm,
  },
  kvCell: {
    width: '47%',
    flexGrow: 1,
    minWidth: 0,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  kvCellLabel: {
    marginBottom: 4,
    color: ticketColors.ink3,
    fontSize: 12,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
  },
  kvCellValue: {
    color: ticketColors.ink,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  kvCellValueMono: {
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.mono,
    fontSize: 12,
  },
  kvCellValueMuted: {
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  metricRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  metric: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
  },
  metricLast: {
    borderRightWidth: 0,
  },
  metricLabel: {
    marginBottom: 4,
    color: ticketColors.ink3,
    fontSize: 12,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  metricValue: {
    color: ticketColors.ink,
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.mono,
  },
  metricValueCompact: {
    fontSize: 14,
    lineHeight: 18,
  },
  metricValueOk: {
    color: ticketColors.ok,
  },
  metricValueBad: {
    color: ticketColors.bad,
  },
  stockFeedback: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
  },
  stockFeedbackError: {
    borderColor: ticketColors.bad,
  },
  stockFeedbackContent: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  stockFeedbackText: {
    flex: 1,
    minWidth: 0,
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 17,
    fontFamily: ticketFonts.sans,
  },
  stockFeedbackErrorText: {
    color: ticketColors.bad,
  },
  infoLine: {
    color: ticketColors.ink2,
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  detailFooter: {
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  footerLeft: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  footerButton: {
    flexShrink: 0,
  },
  footerEditCompact: {
    flex: 1,
  },
  moreActionsButton: {
    minWidth: 112,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
});
